// src/pages/hr/HROnboarding.jsx
// ============================================================
// Employee Onboarding Module
// Redesigned with Green Theme & Outline Icons
// ============================================================

import React, { useState, useCallback } from "react";
import { HR_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { useOnboardingCandidates, updateOnboardingRecord } from "../../hooks/useRecruitment";
import { toast } from "react-hot-toast";
import {
  UserPlus,
  CheckSquare,
  Clock,
  Inbox,
  CheckCircle2,
  ShieldCheck,
  Check,
  UserCheck,
} from "lucide-react";

const PANEL_LABEL = "HR Panel";

const CHECKLIST_ITEMS = [
  "Send Offer letter",
  "Inform Pooja",
  "Onboarding form",
  "Inform Team lead & sharuk sir",
  "Add to main group",
  "Adding in team group",
  "Send google meet link",
  "Add in the google meet attendence class",
  "Update in Enginneering tracker",
  "Add in Shift roaster",
  "Update the Current employees list",
  "Add in EMS",
  "Add in Zoho cliq",
  "Add in google meet",
];

function computeProgress(checklist = {}) {
  if (!checklist || Object.keys(checklist).length === 0) return 0;
  const done = CHECKLIST_ITEMS.filter((item) => checklist[item]).length;
  return Math.round((done / CHECKLIST_ITEMS.length) * 100);
}

function BgvBadge({ status }) {
  const s = (status || "Not Started").toLowerCase();
  let cls = "ob-bgv--not";
  if (s.includes("cleared") || s.includes("complete")) cls = "ob-bgv--cleared";
  else if (s.includes("progress") || s.includes("initiated")) cls = "ob-bgv--progress";
  return (
    <span className={`ob-bgv ${cls}`}>
      <ShieldCheck size={12} strokeWidth={2} /> BGV: {status || "Not Started"}
    </span>
  );
}

export default function HROnboarding() {
  const { data: joiners, loading } = useOnboardingCandidates();
  const [selected, setSelected] = useState(null);
  const [savingId, setSavingId] = useState(null);

  // Sync selected when live data refreshes so checklist stays current
  const selectedCandidate = selected
    ? joiners.find((j) => j.id === selected.id) || selected
    : null;

  const checklist = selectedCandidate?.onboarding?.checklist || {};
  const bgvStatus = selectedCandidate?.onboarding?.bgvStatus || "Not Started";
  const progress = computeProgress(checklist);

  async function toggleCheck(item) {
    if (!selectedCandidate) return;
    const updatedChecklist = { ...checklist, [item]: !checklist[item] };
    const updatedProgress = computeProgress(updatedChecklist);

    // Optimistic: update selected immediately
    setSelected((prev) => ({
      ...prev,
      onboarding: {
        ...(prev?.onboarding || {}),
        checklist: updatedChecklist,
        progress: updatedProgress,
        bgvStatus: prev?.onboarding?.bgvStatus || "Not Started",
      },
    }));

    setSavingId(selectedCandidate.id);
    try {
      await updateOnboardingRecord(selectedCandidate.id, {
        checklist: updatedChecklist,
        progress: updatedProgress,
        bgvStatus: selectedCandidate?.onboarding?.bgvStatus || "Not Started",
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      toast.error("Failed to save checklist: " + err.message);
    } finally {
      setSavingId(null);
    }
  }

  async function handleBgvChange(newStatus) {
    if (!selectedCandidate) return;
    setSelected((prev) => ({
      ...prev,
      onboarding: { ...(prev?.onboarding || {}), bgvStatus: newStatus },
    }));
    setSavingId(selectedCandidate.id);
    try {
      await updateOnboardingRecord(selectedCandidate.id, {
        checklist,
        progress,
        bgvStatus: newStatus,
        updatedAt: new Date().toISOString(),
      });
      toast.success(`BGV status updated to "${newStatus}"`);
    } catch (err) {
      toast.error("Failed to update BGV status: " + err.message);
    } finally {
      setSavingId(null);
    }
  }

  const formatDate = useCallback((val) => {
    if (!val) return "—";
    const d = val?.toDate ? val.toDate() : new Date(val);
    if (isNaN(d)) return String(val);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  }, []);

  function probationEnd(joiningVal) {
    if (!joiningVal) return "—";
    const d = joiningVal?.toDate ? joiningVal.toDate() : new Date(joiningVal);
    if (isNaN(d)) return "—";
    const end = new Date(d);
    end.setMonth(end.getMonth() + 6);
    return end.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  }

  return (
    <Layout navItems={HR_NAV} panelLabel={PANEL_LABEL}>
      <style>{`
        .ob-header-banner {
          background: #1b4b31;
          border-radius: 16px;
          padding: 1.35rem 1.75rem;
          color: #ffffff;
          box-shadow: 0 4px 20px rgba(27, 75, 49, 0.15);
          margin-bottom: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .ob-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
        @media (max-width: 768px) { .ob-grid { grid-template-columns: 1fr; } }
        .ob-card { 
          background: #ffffff; 
          border: 1px solid #e2e8f0; 
          border-radius: 14px; 
          padding: 1.35rem 1.5rem; 
          box-shadow: 0 2px 10px rgba(0,0,0,0.03); 
          transition: all 0.2s ease;
        }
        .ob-card-title { 
          font-size: 0.98rem; 
          font-weight: 800; 
          color: #0f172a; 
          margin: 0 0 1.1rem; 
          display: flex; 
          align-items: center; 
          gap: 0.55rem; 
        }
        .ob-joiner { 
          padding: 0.85rem 0.75rem; 
          border: 1px solid transparent;
          border-bottom: 1px solid #f1f5f9; 
          cursor: pointer; 
          transition: all 0.15s ease; 
          border-radius: 10px; 
        }
        .ob-joiner:hover { 
          background: #f8fafc; 
          border-color: #e2e8f0;
        }
        .ob-joiner.active { 
          background: #f0fdf4; 
          border: 1px solid #bbf7d0;
          border-left: 3.5px solid #10b981; 
          padding-left: 0.85rem; 
        }
        .ob-joiner:last-child { border-bottom: none; }
        .ob-joiner-name { font-weight: 800; font-size: 0.92rem; color: #0f172a; }
        .ob-joiner-meta { font-size: 0.76rem; color: #64748b; margin-top: 3px; font-weight: 500; }
        .progress-bar { height: 6px; background: #e2e8f0; border-radius: 99px; overflow: hidden; margin-top: 6px; }
        .progress-fill { height: 100%; border-radius: 99px; background: #10b981; transition: width 0.4s; }
        .ob-bgv { 
          display: inline-flex; 
          align-items: center;
          gap: 0.25rem;
          padding: 0.2rem 0.65rem; 
          border-radius: 99px; 
          font-size: 0.72rem; 
          font-weight: 700; 
          white-space: nowrap; 
        }
        .ob-bgv--cleared { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .ob-bgv--progress { background: #f0fdf4; color: #166534; border: 1px solid #86efac; }
        .ob-bgv--not { background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; }
        
        .ob-check-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.45rem; }
        .ob-check-item { 
          display: flex; 
          align-items: center; 
          gap: 0.75rem; 
          padding: 0.55rem 0.75rem; 
          border-radius: 8px; 
          cursor: pointer; 
          font-size: 0.84rem; 
          transition: background 0.15s; 
          border: 1px solid transparent;
        }
        .ob-check-item:hover { 
          background: #f8fafc; 
          border-color: #e2e8f0;
        }
        .ob-check-box { 
          width: 19px; 
          height: 19px; 
          border-radius: 5px; 
          border: 1.8px solid #cbd5e1; 
          background: #ffffff;
          display: flex; 
          align-items: center; 
          justify-content: center; 
          flex-shrink: 0; 
          transition: all 0.15s; 
        }
        .ob-check-box--checked { 
          background: #15803d; 
          border-color: #15803d; 
          color: #ffffff; 
        }
        .ob-check-label { color: #1e293b; font-weight: 600; font-size: 0.83rem; }
        .ob-check-label--done { text-decoration: line-through; color: #94a3b8; }
        .ob-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 2.75rem 1rem; gap: 0.75rem; text-align: center; }
        .ob-empty-text { font-size: 0.83rem; color: #64748b; max-width: 320px; line-height: 1.5; font-weight: 500; }
        .ob-skeleton { background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%); background-size: 200% 100%; animation: ob-shimmer 1.2s infinite; border-radius: 8px; height: 64px; margin-bottom: 0.75rem; }
        @keyframes ob-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>

      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "1rem",
        marginBottom: "1.25rem"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <UserCheck size={24} strokeWidth={1.8} color="#1b4b31" />
          <h1 style={{ fontSize: "1.5rem", fontWeight: 800, margin: 0, letterSpacing: "-0.02em", color: "#1b4b31" }}>
            Employee Onboarding
          </h1>
        </div>
      </div>

      <div className="ob-grid">
        {/* ── New Joiners List ── */}
        <div className="ob-card">
          <div className="ob-card-title">
            <UserPlus className="w-5 h-5 text-emerald-700" strokeWidth={1.8} />
            Upcoming New Joiners ({loading ? "…" : joiners.length})
          </div>

          {loading ? (
            <>
              <div className="ob-skeleton" />
              <div className="ob-skeleton" />
              <div className="ob-skeleton" />
            </>
          ) : joiners.length === 0 ? (
            <div className="ob-empty">
              <Inbox className="w-10 h-10 text-slate-300" strokeWidth={1.5} />
              <div className="ob-empty-text">
                No new joiners available. Employees moved to Onboarding will appear here.
              </div>
            </div>
          ) : (
            joiners.map((j) => {
              const liveChecklist = j.onboarding?.checklist || {};
              const liveProgress = j.onboarding?.progress ?? computeProgress(liveChecklist);
              const liveBgv = j.onboarding?.bgvStatus || "Not Started";
              const isActive = selectedCandidate?.id === j.id;
              return (
                <div
                  key={j.id}
                  className={`ob-joiner${isActive ? " active" : ""}`}
                  onClick={() => setSelected(j)}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="ob-joiner-name">{j.name}</div>
                      <div className="ob-joiner-meta">
                        {j.appliedPosition || j.role || "—"} · {j.department || j.dept || "—"}
                        {j.joiningDate ? ` · Joining: ${formatDate(j.joiningDate)}` : ""}
                      </div>
                    </div>
                    <BgvBadge status={liveBgv} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
                    <div className="progress-bar" style={{ flex: 1 }}>
                      <div className="progress-fill" style={{ width: `${liveProgress}%` }} />
                    </div>
                    <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#15803d", minWidth: 32, textAlign: "right" }}>
                      {liveProgress}%
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── Onboarding Checklist ── */}
        <div className="ob-card">
          <div className="ob-card-title">
            <CheckSquare className="w-5 h-5 text-emerald-700" strokeWidth={1.8} />
            Onboarding Checklist
            {selectedCandidate ? (
              <span style={{ fontWeight: 600, color: "#15803d", fontSize: "0.88rem" }}>— {selectedCandidate.name}</span>
            ) : (
              <span style={{ fontWeight: 500, color: "#94a3b8", fontSize: "0.85rem" }}>(Select a Joiner)</span>
            )}
            {savingId === selectedCandidate?.id && (
              <span style={{ marginLeft: "auto", fontSize: "0.72rem", color: "#15803d", fontWeight: 600 }}>Saving…</span>
            )}
          </div>

          {!selectedCandidate ? (
            <div className="ob-empty" style={{ padding: "2.5rem 1rem" }}>
              <CheckCircle2 className="w-10 h-10 text-slate-300" strokeWidth={1.5} />
              <p style={{ color: "#64748b", fontSize: "0.84rem", margin: 0, fontWeight: 500 }}>
                Click on a new joiner on the left to see their checklist.
              </p>
            </div>
          ) : (
            <>
              {/* BGV Status control */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", marginBottom: "1rem", padding: "0.65rem 0.85rem", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#475569" }}>BGV Status:</span>
                <select
                  value={bgvStatus}
                  onChange={(e) => handleBgvChange(e.target.value)}
                  style={{ border: "1.5px solid #cbd5e1", borderRadius: 8, padding: "0.35rem 0.65rem", fontSize: "0.82rem", cursor: "pointer", fontWeight: 600, color: "#0f172a", background: "#ffffff" }}
                >
                  <option>Not Started</option>
                  <option>Initiated</option>
                  <option>In Progress</option>
                  <option>Cleared</option>
                  <option>Failed</option>
                </select>
                <BgvBadge status={bgvStatus} />
              </div>

              {/* Progress summary */}
              <div style={{ marginBottom: "0.85rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 600 }}>Overall Progress</span>
                  <span style={{ fontSize: "0.78rem", fontWeight: 800, color: "#15803d" }}>{progress}%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }} />
                </div>
              </div>

              {/* Checklist items */}
              <ul className="ob-check-list">
                {CHECKLIST_ITEMS.map((item) => (
                  <li key={item} className="ob-check-item" onClick={() => toggleCheck(item)}>
                    <div className={`ob-check-box ${checklist[item] ? "ob-check-box--checked" : ""}`}>
                      {checklist[item] && <Check size={13} strokeWidth={3} />}
                    </div>
                    <span className={`ob-check-label ${checklist[item] ? "ob-check-label--done" : ""}`}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* ── Probation Tracker ── */}
      <div className="ob-card" style={{ marginTop: "1.25rem" }}>
        <div className="ob-card-title">
          <Clock className="w-5 h-5 text-emerald-700" strokeWidth={1.8} />
          Probation Tracker
        </div>
        {loading ? (
          <div className="ob-skeleton" style={{ height: 80 }} />
        ) : joiners.length === 0 ? (
          <div className="ob-empty" style={{ padding: "1.5rem" }}>
            <div className="ob-empty-text">No probation records. New joiners will appear here after they are moved to Onboarding.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.84rem" }}>
              <thead>
                <tr>
                  {["Employee", "Role", "Department", "Joining Date", "Probation End", "Status"].map((h) => (
                    <th key={h} style={{
                      textAlign: "left", padding: "0.65rem 0.85rem",
                      background: "#f8fafc",
                      borderBottom: "1.5px solid #e2e8f0",
                      fontSize: "0.75rem", fontWeight: 700,
                      color: "#475569", textTransform: "uppercase",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {joiners.map((j) => (
                  <tr key={j.id} style={{ transition: "background 0.15s" }}>
                    <td style={{ padding: "0.75rem 0.85rem", fontWeight: 700, color: "#0f172a", borderBottom: "1px solid #f1f5f9" }}>{j.name}</td>
                    <td style={{ padding: "0.75rem 0.85rem", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{j.appliedPosition || j.role || "—"}</td>
                    <td style={{ padding: "0.75rem 0.85rem", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{j.department || j.dept || "—"}</td>
                    <td style={{ padding: "0.75rem 0.85rem", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{formatDate(j.joiningDate || j.joining)}</td>
                    <td style={{ padding: "0.75rem 0.85rem", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{probationEnd(j.joiningDate || j.joining)}</td>
                    <td style={{ padding: "0.75rem 0.85rem", borderBottom: "1px solid #f1f5f9" }}>
                      <span style={{ background: "#f0fdf4", color: "#15803d", border: "1.5px solid #bbf7d0", padding: "0.2rem 0.65rem", borderRadius: 99, fontSize: "0.72rem", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                        <Clock size={11} strokeWidth={2} /> On Probation
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
